import { useEffect, useState } from "react";
import { AlertOctagon, Clock, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";

/**
 * 149-cw-r105 (2026-08-28) — Dashboard widget: invoice yang butuh action FAT.
 * Menampilkan 2 kategori:
 *   - `overdue_promise`: janji bayar terakhir lewat, uang belum masuk (rose)
 *   - `follow_up_needed`: follow_up_date lewat, belum ada janji bayar (amber)
 * Klik item → arahkan ke Monitoring AR tab dengan filter project.
 */
export default function OverduePromiseWidget() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], count: 0, retention_items: [], retention_count: 0, as_of: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reminders/overdue-promises")
      .then(({ data }) => { setData(data || { items: [], count: 0, retention_items: [], retention_count: 0 }); })
      .catch(() => setData({ items: [], count: 0, retention_items: [], retention_count: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const items = data.items || [];
  const retItems = data.retention_items || [];
  const overdueCount = items.filter((x) => x.kind === "overdue_promise").length;
  const followUpCount = items.filter((x) => x.kind === "follow_up_needed").length;
  const totalOutstanding = items.reduce((s, x) => s + (x.amount || 0), 0);
  const totalItems = items.length + retItems.length;

  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="overdue-promise-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline text-slate-500">Follow-Up Client · Butuh Action FAT</p>
          <p className="text-[9px] text-slate-400 italic">Scope: janji bayar lewat & perlu follow-up — bukan total piutang jatuh tempo (<button type="button" className="underline hover:text-[#FF5A1F]" onClick={() => nav("/finance-report/rekonsiliasi-ar")} data-testid="overdue-promise-open-ar-recon">Rekonsiliasi A.R.</button>)</p>
          <h3 className="font-display text-lg tracking-tight text-[#0B1D3A] mt-0.5 flex items-center gap-2">
            <AlertOctagon size={18} className="text-rose-600" />
            {loading ? "Memuat..." : `${totalItems} Item`}
          </h3>
        </div>
        <button
          data-testid="overdue-promise-open-ar"
          onClick={() => nav("/projects?tab=ar")}
          className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-[#FF5A1F] flex items-center gap-1"
        >
          Lihat AR <ExternalLink size={11} />
        </button>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3 text-center">
          <div className="border border-rose-300 bg-rose-50 py-2" data-testid="overdue-promise-count-late">
            <div className="text-lg font-bold text-rose-700 tabular-nums">{overdueCount}</div>
            <div className="text-[9px] uppercase tracking-wider text-rose-600 font-semibold">Janji Lewat</div>
          </div>
          <div className="border border-amber-300 bg-amber-50 py-2" data-testid="overdue-promise-count-followup">
            <div className="text-lg font-bold text-amber-700 tabular-nums">{followUpCount}</div>
            <div className="text-[9px] uppercase tracking-wider text-amber-700 font-semibold">Perlu FU</div>
          </div>
          <div className="border border-slate-300 bg-slate-50 py-2" data-testid="overdue-promise-outstanding">
            <div className="text-sm font-bold text-slate-700 tabular-nums">{formatIDR(totalOutstanding)}</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-600 font-semibold">Outstanding</div>
          </div>
        </div>
      )}

      {loading && <div className="text-xs italic text-slate-400 text-center py-4">Memuat data...</div>}
      {!loading && items.length === 0 && retItems.length === 0 && (
        <div className="text-xs italic text-slate-400 text-center py-6" data-testid="overdue-promise-empty">
          ✓ Tidak ada invoice yang lewat janji bayar / retensi jatuh tempo hari ini
        </div>
      )}
      {!loading && retItems.length > 0 && (
        <div className="mb-3" data-testid="retention-due-list">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1 flex items-center gap-1">
            <AlertOctagon size={10} className="text-sky-700" /> Retensi Jatuh Tempo ({retItems.length})
          </div>
          <ol className="space-y-1 max-h-40 overflow-y-auto border border-sky-200 bg-sky-50/40">
            {retItems.slice(0, 10).map((it) => (
              <li
                key={`ret-${it.project_id}`}
                className="border-l-4 border-l-sky-500 pl-3 pr-2 py-1.5 text-xs hover:bg-sky-50 cursor-pointer"
                onClick={() => nav(`/projects?tab=wo&project_id=${it.project_id}`)}
                data-testid={`retention-item-${it.project_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0B1D3A] truncate">{it.project_name || "-"}</div>
                    <div className="text-[10px] text-slate-500 truncate">{it.client || "-"} · Retensi {it.retention_pct}%</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-slate-800 tabular-nums text-[11px]">{formatIDR(it.estimated_amount)}</div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-sky-700 flex items-center gap-1 justify-end">
                      <Clock size={9} /> {it.days_overdue}h Lewat
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Jatuh Tempo: <span className="font-semibold text-sky-700">{fmtDate(it.retention_due_date)}</span> — buat Invoice 5%
                </div>
              </li>
            ))}
            {retItems.length > 10 && <li className="text-[10px] italic text-slate-400 text-center pt-2">+{retItems.length - 10} retensi lainnya</li>}
          </ol>
        </div>
      )}
      {!loading && items.length > 0 && (
        <ol className="space-y-1 max-h-72 overflow-y-auto" data-testid="overdue-promise-list">
          {items.slice(0, 15).map((it) => {
            const isOverdue = it.kind === "overdue_promise";
            return (
              <li
                key={`${it.entry_id}-${it.kind}`}
                className={`border-l-4 pl-3 pr-2 py-1.5 text-xs hover:bg-slate-50 cursor-pointer ${isOverdue ? "border-l-rose-500 bg-rose-50/40" : "border-l-amber-500 bg-amber-50/40"}`}
                onClick={() => nav(`/projects?tab=ar&project_id=${it.project_id}`)}
                data-testid={`overdue-promise-item-${it.entry_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0B1D3A] truncate">{it.project_name || "-"}</div>
                    <div className="text-[10px] text-slate-500 truncate">{it.client || "-"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-slate-800 tabular-nums text-[11px]">{formatIDR(it.amount)}</div>
                    <div className={`text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 justify-end ${isOverdue ? "text-rose-700" : "text-amber-700"}`}>
                      <Clock size={9} />
                      {it.days_overdue} hari {isOverdue ? "Terlambat" : "FU"}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {isOverdue
                    ? <>Janji v{it.promise_version}: <span className="font-semibold text-rose-700">{fmtDate(it.latest_promise_date)}</span>{it.latest_promise_note ? ` · ${it.latest_promise_note}` : ""}</>
                    : <>Follow-up: <span className="font-semibold text-amber-700">{fmtDate(it.follow_up_date)}</span></>}
                </div>
              </li>
            );
          })}
          {items.length > 15 && (
            <li className="text-[10px] italic text-slate-400 text-center pt-2">+{items.length - 15} invoice lainnya — buka Monitoring AR</li>
          )}
        </ol>
      )}
    </div>
  );
}
